import {
  Client,
  DataCollector,
  ThreeDSecure,
  ThreeDSecureVerifyPayload,
  client,
  dataCollector,
  threeDSecure,
} from 'braintree-web';
import { TFunction } from 'i18next';

import { ThreeDSecureVerifyPayloadOverride } from '@features/BraintreeAuthorization/BraintreeAuthorizer/BraintreeAuthorizer';
import {
  Additional3dsInfo,
  CardType,
  CreditCardType,
  FraudPreventionRecommendation,
} from '@types-internal';
import { TrackingFn } from '@types-internal/TrackingTypes';
import {
  chunks,
  convertAddress,
  digitsOnly,
  formatAdditionalInformation,
  formatCustomFields,
} from '@utils/braintreeUtils';
import { checkFeatureForUser } from '@utils/utils';

import { braintreeTracking } from './diiService';
import { captureException } from './sentryService';

// Some of the possible values: https://developer.paypal.com/braintree/docs/reference/response/credit-card#card_type
// Note that it is not comprehensive (ex: UK Maestro is missing)
const NICE_TYPES_TO_TYPE: { [key: string]: CardType } = {
  visa: 'visa',
  mastercard: 'mastercard',
  americanexpress: 'american-express',
  dinersclub: 'diners-club',
  discover: 'discover',
  jcb: 'jcb',
  unionpay: 'unionpay',
  ukmaestro: 'maestro',
  maestro: 'maestro',
  elo: 'elo',
  mir: 'mir',
  hiper: 'hiper',
  hipercard: 'hipercard',
};

export const sanitizeCardType = (inputType: string) => {
  const mapping =
    NICE_TYPES_TO_TYPE[
      inputType.replace(' ', '').replace('-', '').toLowerCase() as keyof typeof NICE_TYPES_TO_TYPE
    ];
  if (!mapping) {
    throw new Error('Cannot convert credit card niceType : ' + inputType);
  }
  return mapping;
};

export const sanitizeCardTypes = (inputTypes: string[]) => {
  return [...new Set(inputTypes.map((type) => sanitizeCardType(type)))];
};

const maybeTrackFrictionless = (
  threeDSecureVerifyPayload: ThreeDSecureVerifyPayloadOverride,
): void => {
  if (
    /* frictionless authentication doesn't contain acs url in the response */
    !threeDSecureVerifyPayload.threeDSecureInfo.acsUrl &&
    threeDSecureVerifyPayload.threeDSecureInfo.status === 'authenticate_successful' &&
    threeDSecureVerifyPayload.threeDSecureInfo.liabilityShifted
  )
    braintreeTracking('3DSfrictionless');
};

export type AuthorizePaymentResultError = {
  success: false;
  errorMessage: string;
  retryable: boolean;
};

export const authorizePaymentResult3DSSucessStatuses = [
  'authenticate_successful', // 3DS success
  'authenticate_attempt_successful', // 3DS success
  'authenticate_successful_issuer_not_participating', // 3DS success
  'authentication_bypassed', // 3DS success
];

export const authorizePaymentResultExemptionStatuses = [
  'exemption_low_value_successful', // 3DS exemption
  'exemption_tra_successful', // 3DS exemption
  'skipped_due_to_rule', // 3DS exemption
];

export const authorizePaymentResult3DSSucessFailStatuses = [
  'authenticate_frictionless_failed', // 3DS fail
  'authentication_unavailable', // 3DS fail
  'authenticate_rejected', // 3DS fail
  'lookup_bypassed', // 3DS fail
  'challenge_required', // 3DS fail
];

const authorizePaymentResultSuccessStatuses = [
  ...authorizePaymentResult3DSSucessStatuses,
  ...authorizePaymentResultExemptionStatuses,
];

type AuthorizePaymentResultSuccessStatus = (typeof authorizePaymentResultSuccessStatuses)[number];

type AuthorizePaymentResultSuccess = {
  success: true;
  nonce: string;
  liabilityShifted: boolean;
  liabilityShiftPossible: boolean;
  status: AuthorizePaymentResultSuccessStatus;
  deviceData: string;
};

export type AuthorizePaymentResult = AuthorizePaymentResultError | AuthorizePaymentResultSuccess;

export type AuthorizePaymentRequest = {
  t: TFunction;
  token: string;
  bin: string;
  nonce: string;
  amount: number;
  additional3dsInfo?: Additional3dsInfo;
  cardType: CreditCardType;
  isSubscriptionFirst: boolean;
  subscriptionFirstInterval: string;
  customerId: string | undefined;
  fraudPreventionRecommendation?: FraudPreventionRecommendation;
  siteId: number;
  optimizelyId: string;
  track?: TrackingFn;
};

export const authorizePayment = async ({
  t,
  token,
  bin,
  nonce,
  amount,
  additional3dsInfo,
  cardType,
  isSubscriptionFirst,
  subscriptionFirstInterval,
  customerId,
  fraudPreventionRecommendation,
  siteId,
  optimizelyId,
  track,
}: AuthorizePaymentRequest): Promise<AuthorizePaymentResult> => {
  const buildInitializationError = (): AuthorizePaymentResultError => {
    return {
      success: false,
      errorMessage: t('checkout.payment.creditcard.error.method.unavailable'),
      retryable: false,
    };
  };
  const buildRetryError = (): AuthorizePaymentResultError => {
    return {
      success: false,
      errorMessage: t('checkout.payment.creditcard.error.authorize.retry'),
      retryable: true,
    };
  };
  const buildNonRetryError = (): AuthorizePaymentResultError => {
    return {
      success: false,
      errorMessage: t('checkout.payment.creditcard.error.authorize.non.retry'),
      retryable: false,
    };
  };

  track?.('cardType', cardType);
  track?.('subscriptionFirst', String(isSubscriptionFirst));

  if (!track) {
    braintreeTracking('cardType', cardType);
    braintreeTracking('subscriptionFirst', String(isSubscriptionFirst));
  }

  let braintreeClient: Client;
  try {
    braintreeClient = await client.create({ authorization: token });
  } catch (error: any) {
    captureException({
      error,
      message: 'Cannot create Braintree client',
    });
    return buildInitializationError();
  }

  let braintreeDataCollector: DataCollector;
  try {
    braintreeDataCollector = await dataCollector.create({
      client: braintreeClient,
    });
  } catch (error: any) {
    captureException({
      error,
      message: 'Data collector cannot be created',
    });
    return buildInitializationError();
  }

  let threeDSecureInstance: ThreeDSecure;
  try {
    threeDSecureInstance = await threeDSecure.create({
      authorization: token,
      version: 2,
    });
    threeDSecureInstance.on('authentication-modal-render', () => {
      track?.('Braintree_3DSWindow_Displayed');
      if (!track) {
        braintreeTracking('3DSdisplay');
      }
    });
    threeDSecureInstance.on('customer-canceled', () => {
      track?.('Click_3DSCancel');
      if (!track) {
        braintreeTracking('3DScancel');
      }
    });
  } catch (error: any) {
    captureException({
      error,
      message: '3DSecure instance cannot be created',
    });
    return buildInitializationError();
  }

  if (!nonce) return buildInitializationError();

  const userAttributes = {
    siteId,
  };

  const force3dsChallengeEnabled = await checkFeatureForUser(
    'braintree_cc_force_3ds_challenge_enabled',
    userAttributes,
    optimizelyId,
  );

  const force3dsChallenge =
    cardType === 'TO_SAVE' ||
    isSubscriptionFirst ||
    (fraudPreventionRecommendation === 'REVIEW' && force3dsChallengeEnabled);
  let basePayload: ThreeDSecureVerifyPayload;

  try {
    // Documentation: https://braintree.github.io/braintree-web/current/ThreeDSecure.html#verifyCard
    basePayload = await threeDSecureInstance.verifyCard({
      challengeRequested: force3dsChallenge,
      nonce,
      bin,
      amount,
      email: chunks(additional3dsInfo?.email, 255),
      mobilePhoneNumber: chunks(digitsOnly(additional3dsInfo?.shippingAddress?.phoneNumber), 25),
      billingAddress: convertAddress('billing', additional3dsInfo?.billingAddress),
      additionalInformation: formatAdditionalInformation(
        isSubscriptionFirst,
        subscriptionFirstInterval,
        additional3dsInfo,
      ),
      customFields: formatCustomFields(
        cardType,
        isSubscriptionFirst,
        customerId,
        fraudPreventionRecommendation,
        String(force3dsChallengeEnabled) as 'true' | 'false',
      ),
      onLookupComplete: (_data: unknown, next: () => void) => {
        next();
      },
    } as any);

    if (force3dsChallenge) {
      track?.('force3DSchallenge');
      if (!track) {
        braintreeTracking('force3DSchallenge');
      }
    }
  } catch (error: any) {
    track?.('3DSsubmitKObackend', error);
    if (!track) {
      braintreeTracking('3DSsubmitKObackend', error);
    }
    // Error on Authentication
    // >>> An error occurred while attempting to authenticate. Alternatively, merchants can ask customers for an alternative form of payment.
    // Test card: 4000000000001125 (VISA)
    // Action: Show message Technical error, please try again - if error persists, try a different payment method.
    captureException({
      error,
      message: 'An internal error happened during 3DS',
    });
    return buildRetryError();
  }

  const paymentMethodPayload = basePayload as any as ThreeDSecureVerifyPayloadOverride;
  const { threeDSecureInfo } = paymentMethodPayload;
  maybeTrackFrictionless(paymentMethodPayload);

  // https://wiki.web.zooplus.de/display/PD/Error+Handling+during+Braintree+payment+process
  switch (threeDSecureInfo.status) {
    case 'authenticate_successful':
      // Successful No-Challenge Authentication
      // >>> Cardholder enrolled, authentication successful, and signature verification successful.
      // Test card: 4000000000001000 (VISA)

      // Successful Challenge Authentication
      // >>> Cardholder enrolled, authentication successful, and signature verification successful.
      // Test card: 4000000000001091 (VISA)

      // Action: success, proceed with order creation
      track?.('3DSsubmitOK', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      }
      break;
    case 'authenticate_attempt_successful':
      //Attempt No-Challenge Authentication
      /**
       * The provided card brand authenticated this 3D Secure transaction without password confirmation
       * from the customer.
       *  */

      // Test card: 4000000000001026    MM/YYY: 01/20**
      // Action: success, proceed with order creation
      // Liability Shifted: True
      track?.('3DSsubmitOK', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      }
      break;
    case 'authenticate_successful_issuer_not_participating': // deprecated on BT side but we are handling it just in case
      /**
       * Your customer’s card-issuing bank is domiciled outside of the US and is not certified by the associated card brand to allow
       * 3D Secure transactions.
       */
      // Test card: No testcard is provided by Braintree
      // Action: success, proceed with order creation
      // Liability Shifted: True
      track?.('3DSsubmitOK', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      }
      break;
    case 'authentication_bypassed':
      /**
       * The customer's card was issued by a bank where 3D Secure authentication steps are bypassed. If you prefer not to continue
       * transactions with this status, ask the customer for a different payment method.
       */
      // Test card: No testcard is provided by Braintree
      // Action: success, proceed with order creation
      // Liability Shifted: True
      track?.('3DSsubmitOK', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      }
      break;
    case 'exemption_low_value_successful':
      /**
       * Successfully applied a low value exemption.
       * See also "Low-value transactions": https://www.braintreepayments.com/de/resources/psd2-strong-customer-authentication-explained
       */
      // Test card: No testcard is provided by Braintree
      // Action: success, proceed with order creation
      // Liability Shifted: False
      track?.('3DSskipOK', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSskipOK', threeDSecureInfo.status);
      }
      break;
    case 'exemption_tra_successful':
      /**
       * Successfully applied a transaction risk analysis exemption.
       * See also "Transaction risk analysis for low-risk transactions": https://www.braintreepayments.com/de/resources/psd2-strong-customer-authentication-explained
       */
      // Test card: No testcard is provided by Braintree
      // Action: success, proceed with order creation
      // Liability Shifted: False
      track?.('3DSskipOK', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSskipOK', threeDSecureInfo.status);
      }
      break;
    case 'skipped_due_to_rule':
      /**
       * 3D Secure was skipped as stated by the matched 3D Secure Rule (only for non-European countries).
       */
      // Test card: No testcard is provided by Braintree
      // Action: success, proceed with order creation
      // Liability Shifted: False
      track?.('3DSskipOK', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSskipOK', threeDSecureInfo.status);
      }
      break;
    case 'authenticate_frictionless_failed':
      // Failed No-Challenge Authentication
      // >>> Cardholder enrolled, authentication unsuccessful. Merchants should prompt customers for another form of payment.
      // Test card: 4000000000001018 (VISA)

      // Action: 3D secure check failed, show error message & ask for different card/payment method
      track?.('3DSsubmitKO', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      }
      return buildRetryError();
    case 'authentication_unavailable':
      // Attempt No-Challenge Authentication
      // >>> The provided card brand authenticated this 3D Secure transaction without password confirmation from the customer.
      // Test card: 4000000000001026 (VISA)

      // Authentication Not Available on Lookup
      // >>> Authentication unavailable for this transaction.
      // Test card: 4000000000001059 (VISA)

      // Error on Lookup
      // >>> An error occurred while attempting to lookup enrollment.
      // Test card: 4000000000001067 (VISA)

      // Timeout on Lookup
      // >>> Attempting to lookup enrollment resulted in a timeout.
      // Test card: 4000000000001075 (VISA)

      // Action: Show message: We could not verify your card, please try again or use a different card / payment method
      track?.('3DSsubmitKO', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      }
      return buildRetryError();
    case 'authenticate_unable_to_authenticate':
      // Unavailable No-Challenge Authentication from the Issuer
      // >>> Authentication unavailable for this transaction.
      // Test card: 4000000000001034 (VISA)

      // Data Only Successful
      // >>>The data-only 3D Secure call was successfully created. The dataOnlyRequested flag must be sent to receive a successful response.
      // @See https://developer.paypal.com/braintree/docs/guides/3d-secure/advanced-options#using-data-only-3d-secure
      // Test card: 5119737947565580 (MASTERCARD)

      // Action: Show message: We could not verify your card, please try again or use a different card / payment method
      track?.('3DSsubmitKO', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      }
      return buildRetryError();
    case 'authenticate_rejected':
      // Rejected No-Challenge Authentication by the Issuer
      // >>> Authentication unsuccessful. Merchants should prompt customers for another form of payment.
      // Test card: 4000000000001042 (VISA)

      // Action: Show message: Verification failed, please use a different card / payment method
      track?.('3DSsubmitKO', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      }
      return buildNonRetryError();
    case 'lookup_bypassed':
      // Bypassed Authentication
      // >>> Bypass used to simulate a scenario where merchant has elected to bypass the consumer authentication flow via CardinalCommerce Rules Engine configuration.
      // Test card: 4000000000001083 (VISA)

      // Action: Show message: We could not verify your card, please try again or use a different card / payment method
      // This cannot happen as we want to have the liability shift so we wont activate this functionality
      track?.('3DSsubmitKO', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      }
      return buildRetryError();
    case 'challenge_required':
      // Failed Challenge Authentication
      // >>> Cardholder enrolled, authentication unsuccessful. Merchants should prompt customers for another form of payment.
      // Test card: 4000000000001109 (VISA)

      // Challenge Authentication is Unavailable
      // >>> Authentication unavailable for this transaction.
      // Test card: 4000000000001117 (VISA)

      // Action: Show message: We could not verify your card, please try again or use a different card / payment method
      track?.('3DSsubmitKO', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      }
      return buildRetryError();
    default:
      track?.('3DSsubmitKO', threeDSecureInfo.status);
      if (!track) {
        braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      }
      return buildNonRetryError();
  }

  const output: AuthorizePaymentResultSuccess = {
    success: true,
    nonce: paymentMethodPayload.nonce,
    liabilityShifted: threeDSecureInfo.liabilityShifted,
    liabilityShiftPossible: threeDSecureInfo.liabilityShiftPossible,
    status: threeDSecureInfo.status,
    deviceData: braintreeDataCollector.deviceData,
  };

  return output;
};
